(() => {
  const ROOT = window.Shopify?.routes?.root || '/';
  const STACK_FLAG = '_bivalta_stack';
  const STACK_ID = '_bivalta_stack_id';
  const STACK_INSTANCE = '_bivalta_stack_instance';
  const STACK_BASE_QTY = '_bivalta_stack_base_qty';
  const stackI18n = window.BivaltaI18n?.stack || {};
  const cartI18n = window.BivaltaI18n?.cart || {};

  let operationInFlight = false;

  const fetchCart = async () => {
    const response = await fetch(ROOT + 'cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error(stackI18n.cartReadFailed || response.statusText || '');
    }

    return response.json();
  };

  const getLine = (target) => target?.closest?.('[data-bivalta-cart-line]');

  const findCartItem = (cart, line) => {
    if (!cart || !Array.isArray(cart.items) || !line) return null;

    const key = String(line.dataset.key || '');
    if (key) {
      const byKey = cart.items.find((item) => String(item.key || '') === key);
      if (byKey) return byKey;
    }

    const lineNumber = Number(line.dataset.line || 0);
    if (lineNumber > 0) return cart.items[lineNumber - 1] || null;

    return null;
  };

  const isProtectedStackItem = (item) => {
    const properties = item?.properties || {};

    return String(properties[STACK_FLAG] || '') === '1'
      && Boolean(properties[STACK_ID])
      && Boolean(properties[STACK_INSTANCE]);
  };

  const stackBaseQuantity = (item) => {
    const configured = Number(item?.properties?.[STACK_BASE_QTY] || 0);
    if (Number.isInteger(configured) && configured > 0) return configured;

    // Current Bivalta Stack definitions add one unit of each selected product.
    return 1;
  };

  const setLineBusy = (line, busy) => {
    if (!line) return;

    line.classList.toggle('is-loading', busy);

    line.querySelectorAll('button, input').forEach((element) => {
      element.disabled = busy;
    });
  };

  const parseResponseError = async (response, fallback) => {
    try {
      const data = await response.json();
      return data?.description || data?.message || fallback;
    } catch (_) {
      return fallback;
    }
  };

  const changeLineQuantity = async (lineKey, quantity) => {
    const response = await fetch(ROOT + 'cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: lineKey,
        quantity: Math.max(0, Number(quantity || 0))
      })
    });

    if (!response.ok) {
      throw new Error(
        await parseResponseError(
          response,
          stackI18n.quantityUpdateFailed || response.statusText || ''
        )
      );
    }

    return response.json();
  };

  const addStandaloneVariant = async (variantId, quantity) => {
    const safeQuantity = Math.max(0, Number(quantity || 0));
    if (safeQuantity <= 0) return null;

    const response = await fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [
          {
            id: Number(variantId),
            quantity: safeQuantity
          }
        ]
      })
    });

    if (!response.ok) {
      throw new Error(
        await parseResponseError(
          response,
          stackI18n.extraAddFailed || response.statusText || ''
        )
      );
    }

    return response.json();
  };

  const publishCart = (cart) => {
    window.BivaltaCartData?.set?.(cart);

    document.dispatchEvent(
      new CustomEvent('bivalta:cart-updated', {
        detail: { cart }
      })
    );

    document.dispatchEvent(new CustomEvent('bivalta:cart-change'));
  };

  const refreshCartUi = async () => {
    const cart = await fetchCart();
    publishCart(cart);

    if (document.querySelector('[data-bivalta-cart-page]')) {
      window.location.reload();
      return;
    }

    if (window.BivaltaCartPage?.isDrawerOpen?.()) {
      await window.BivaltaCartPage.openDrawer();
      return;
    }

    if (window.BivaltaCartPage?.openDrawer) {
      await window.BivaltaCartPage.openDrawer();
    }
  };

  const repairStackAndAddStandalone = async (line, item, extraQuantity) => {
    const baseQuantity = stackBaseQuantity(item);
    const currentQuantity = Math.max(0, Number(item?.quantity || 0));
    const alreadyMergedExtra = Math.max(0, currentQuantity - baseQuantity);
    const totalStandaloneToAdd = alreadyMergedExtra + Math.max(0, Number(extraQuantity || 0));

    if (currentQuantity !== baseQuantity) {
      await changeLineQuantity(item.key, baseQuantity);
    }

    if (totalStandaloneToAdd > 0) {
      await addStandaloneVariant(item.variant_id, totalStandaloneToAdd);
    }
  };

  const handlePlus = async (line) => {
    const cart = await fetchCart();
    const item = findCartItem(cart, line);

    if (!item) {
      throw new Error(stackI18n.lineNotFound || '');
    }

    if (isProtectedStackItem(item)) {
      // The discounted Stack line stays fixed. Any extra unit becomes
      // a normal cart line without private Stack properties.
      await repairStackAndAddStandalone(line, item, 1);
      return;
    }

    await changeLineQuantity(
      item.key,
      Math.max(0, Number(item.quantity || 0)) + 1
    );
  };

  const handleQuantityInput = async (line, requestedQuantity) => {
    const cart = await fetchCart();
    const item = findCartItem(cart, line);

    if (!item) {
      throw new Error(stackI18n.lineNotFound || '');
    }

    const requested = Math.max(0, Number(requestedQuantity || 0));

    if (!isProtectedStackItem(item)) {
      await changeLineQuantity(item.key, requested);
      return;
    }

    const baseQuantity = stackBaseQuantity(item);

    if (requested <= baseQuantity) {
      // 0 removes the original Stack item and therefore correctly
      // invalidates the Stack discount. 1 keeps the original Stack intact.
      await changeLineQuantity(item.key, requested);
      return;
    }

    // Never increase the protected Stack line itself. Keep its base
    // quantity and move the requested surplus to a standalone line.
    const currentQuantity = Math.max(0, Number(item.quantity || 0));
    if (currentQuantity !== baseQuantity) {
      await changeLineQuantity(item.key, baseQuantity);
    }

    await addStandaloneVariant(
      item.variant_id,
      requested - baseQuantity
    );
  };

  const runOperation = async (line, operation) => {
    if (operationInFlight) return;

    operationInFlight = true;
    setLineBusy(line, true);

    try {
      await operation();
      await refreshCartUi();
    } catch (error) {
      console.error('Bivalta Stack Cart Protection:', error);
      const message = error?.message || cartI18n.updateFailed || '';
      if (message) {
        window.BivaltaToast?.(
          message,
          'error'
        );
      }

      try {
        await refreshCartUi();
      } catch (_) {}
    } finally {
      operationInFlight = false;
      setLineBusy(line, false);
    }
  };

  /*
   * IMPORTANT:
   * Capture every cart "+" synchronously before bivalta-cart.js can
   * change the tagged Stack line. We then reproduce the normal +
   * behavior for ordinary lines, and split extras for Stack lines.
   */
  document.addEventListener('click', (event) => {
    const plusButton = event.target.closest?.('[data-bivalta-cart-plus]');
    if (!plusButton) return;

    const line = getLine(plusButton);
    if (!line) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    runOperation(line, () => handlePlus(line));
  }, true);

  /*
   * The same rule applies when a quantity is typed manually.
   * We stop the theme's default change handler first, then decide
   * server-side whether the line is protected or ordinary.
   */
  document.addEventListener('change', (event) => {
    const input = event.target.closest?.('[data-bivalta-cart-quantity]');
    if (!input) return;

    const line = getLine(input);
    if (!line) return;

    const requested = Math.max(
      0,
      Number.parseInt(String(input.value || 0), 10) || 0
    );

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    runOperation(
      line,
      () => handleQuantityInput(line, requested)
    );
  }, true);
})();